import React, { createContext, useContext, useState, useMemo } from 'react';
import { EditorThemeTokens, EDITOR_DEFAULT_THEME, STORAGE_KEYS, tokensToCssVars } from './tokens';

interface EditorThemeContextType {
  theme: EditorThemeTokens;
  updateTheme: (partial: Partial<EditorThemeTokens>) => void;
  resetTheme: () => void;
}

const EditorThemeContext = createContext<EditorThemeContextType>({
  theme: EDITOR_DEFAULT_THEME,
  updateTheme: () => {},
  resetTheme: () => {},
});

export const useEditorTheme = () => useContext(EditorThemeContext);

interface EditorThemeProviderProps {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}

export const EditorThemeProvider: React.FC<EditorThemeProviderProps> = ({
  children,
  className = '',
  style,
}) => {
  const [theme, setTheme] = useState<EditorThemeTokens>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.EDITOR_THEME);
      if (stored) {
        return { ...EDITOR_DEFAULT_THEME, ...JSON.parse(stored) };
      }
    } catch {
      // Fallback
    }
    return EDITOR_DEFAULT_THEME;
  });

  const updateTheme = (partial: Partial<EditorThemeTokens>) => {
    setTheme((prev) => {
      const next = { ...prev, ...partial };
      try {
        localStorage.setItem(STORAGE_KEYS.EDITOR_THEME, JSON.stringify(next));
      } catch {
        // Fallback
      }
      return next;
    });
  };

  const resetTheme = () => {
    setTheme(EDITOR_DEFAULT_THEME);
    try {
      localStorage.removeItem(STORAGE_KEYS.EDITOR_THEME);
    } catch {
      // Fallback
    }
  };

  const cssStyle = useMemo(() => {
    return {
      ...tokensToCssVars(theme as unknown as Record<string, string>),
      ...style,
    };
  }, [theme, style]);

  return (
    <EditorThemeContext.Provider value={{ theme, updateTheme, resetTheme }}>
      <div className={`editor-shell ${className}`} style={cssStyle}>
        {children}
      </div>
    </EditorThemeContext.Provider>
  );
};
